// IE Hub - Application Entry Point
// Main entry point with React Router setup

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createHashRouter, RouterProvider } from "react-router-dom";

import AppShell, { PUBLIC_ROUTE_PREFIXES } from "./app/AppShell";
import { routes } from "./app/routes/index";
import ErrorBoundary from "./app/routes/ErrorBoundary";
import { initClarity, setClarityMaskAll } from "./shared/logging/clarity";
import registerChunkReloadGuard from "./shared/lib/registerChunkReload";
import { installFailureReporter } from "./shared/api/failureReporter";

// Import global styles
import "./index.css";
// Bundle local Roboto font
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

// Legacy redirect: old "#/signup" should land on "#/auth" (prevents 404 in HashRouter)
const hash = window.location.hash;
if (
  hash === "#/signup" ||
  hash.startsWith("#/signup?") ||
  hash.startsWith("#/signup/")
) {
  window.location.replace(`/#/auth${hash.slice("#/signup".length)}`); // preserves ?query if present
}

// Service Worker registration for Firebase messaging.
// Gated on secure context — SW evaluation fails on http://, and Firebase's
// importScripts() requires https + reachable gstatic.com. Swallow errors so a
// failing SW never surfaces as an uncaught rejection.
if (
  "serviceWorker" in navigator &&
  (window.isSecureContext || window.location.hostname === "localhost")
) {
  navigator.serviceWorker
    .register("/firebase-messaging-sw.js")
    .catch((err) => console.warn("FCM SW registration skipped:", err?.message || err));
}

// Normalize URL for HashRouter: if someone hits /something#/path, redirect to /#/path
// This keeps a single canonical shape for all routes in dev and prod
if (window.location.pathname !== "/" && window.location.hash) {
  const normalizedUrl = `/${window.location.hash}`; // preserves the hash including any query after it
  window.location.replace(normalizedUrl);
}

// Pre-mask <body> for Microsoft Clarity before the recorder loads, so a direct
// deep-link into an authenticated route never leaks its first frame. AppShell
// keeps this in sync on subsequent client-side navigations. (HashRouter: the
// route lives in the URL hash.)
const initialPath = window.location.hash.replace(/^#/, "").split("?")[0] || "/";
const isInitialPublicRoute = PUBLIC_ROUTE_PREFIXES.some((prefix) =>
  initialPath.startsWith(prefix)
);
setClarityMaskAll(!isInitialPublicRoute);

// Initialize Microsoft Clarity (opt-in via env + project id)
void initClarity();

// Recover from stale chunk URLs by reloading once per session
registerChunkReloadGuard();

// Forward axios failures to backend ApiFailureLog (additive — does not alter
// existing femaCapture / 401 refresh behavior).
installFailureReporter();

// Create router configuration (HashRouter for environment-agnostic deep links)
const router = createHashRouter(
  [
    {
      path: "/",
      element: <AppShell />,
      errorElement: (
        <ErrorBoundary>
          <div />
        </ErrorBoundary>
      ),
      children: routes,
    },
  ],
  {
    future: {
      // v7_startTransition: true // Removed due to TypeScript compatibility issues
    },
  }
);

// Get root element with proper type checking
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

// Create root and render app
const root = createRoot(rootElement);

root.render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>
);
